import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { client } from '../api/client';
import { useI18n } from '../i18n';

export function NewsletterUnsubscribePage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const { language } = useI18n();
  
  const [status, setStatus] = useState<'confirm' | 'loading' | 'success' | 'error'>('confirm');
  const [message, setMessage] = useState('');

  const handleUnsubscribe = async () => {
    setStatus('loading');
    
    try {
      const res = await client.api.fetch('/api/newsletter/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token }),
      });

      const data = await res.json();

      if (data.success) {
        setStatus('success');
        setMessage(language === 'sr' 
          ? 'Uspešno ste se odjavili sa newsletter liste. Više nećete primati naše email-ove.' 
          : 'You have successfully unsubscribed from our newsletter. You will no longer receive our emails.');
      } else {
        setStatus('error');
        setMessage(data.error || (language === 'sr' 
          ? 'Odjava nije uspela. Pokušajte ponovo ili nas kontaktirajte.' 
          : 'Unsubscribe failed. Please try again or contact us.'));
      }
    } catch (err) {
      setStatus('error');
      setMessage(language === 'sr' 
        ? 'Greška pri komunikaciji sa serverom. Pokušajte ponovo.' 
        : 'Error communicating with server. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-16 flex items-center justify-center">
      <div className="max-w-md mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 text-center"
        >
          {status === 'confirm' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Odjava sa newsletter-a' : 'Unsubscribe from Newsletter'}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {language === 'sr' 
                  ? `Da li ste sigurni da želite da se odjavite sa newsletter liste${email ? ` (${email})` : ''}?` 
                  : `Are you sure you want to unsubscribe from our newsletter${email ? ` (${email})` : ''}?`}
              </p>
              <div className="space-y-3">
                <button 
                  onClick={handleUnsubscribe}
                  className="w-full px-6 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors"
                >
                  {language === 'sr' ? 'Da, odjavi me' : 'Yes, Unsubscribe'}
                </button>
                <Link 
                  to="/" 
                  className="block text-slate-600 dark:text-slate-400 hover:text-amber-500 transition-colors"
                >
                  {language === 'sr' ? 'Ne, vrati me nazad' : 'No, Take Me Back'}
                </Link>
              </div>
            </>
          )}

          {status === 'loading' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Odjava u toku...' : 'Unsubscribing...'}
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                {language === 'sr' ? 'Molimo sačekajte.' : 'Please wait.'}
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Uspešno odjavljeni' : 'Successfully Unsubscribed'}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mb-6">{message}</p>
              <Link 
                to="/" 
                className="inline-block px-6 py-3 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
              >
                {language === 'sr' ? 'Nazad na početnu' : 'Back to Home'}
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                {language === 'sr' ? 'Greška' : 'Error'}
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mb-6">{message}</p>
              <div className="space-y-3">
                <button 
                  onClick={() => setStatus('confirm')}
                  className="block w-full px-6 py-3 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors"
                >
                  {language === 'sr' ? 'Pokušaj ponovo' : 'Try Again'}
                </button>
                <Link 
                  to="/" 
                  className="block text-slate-600 dark:text-slate-400 hover:text-amber-500 transition-colors"
                >
                  {language === 'sr' ? 'Nazad na početnu' : 'Back to Home'}
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
